import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';
import { getMyReferralCode, ReferralCode } from '~/services/referral/referralCode';
import { referralLink } from '~/utils/referral';
import { formatAmount } from '~/utils/trialPricing';
import { unixDate } from '~/utils/pauseFormat';

import styles from './Account.redesign.module.css';
import own from './ReferralCard.module.css';

function IconGift() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect x="3" y="8" width="18" height="4" rx="1" />
            <path d="M12 8v13M19 12v9H5v-9" />
            <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5" />
        </svg>
    );
}

/**
 * Carte « Parrainez un établissement : 1 mois offert chacun » (page Mon compte). Le code du lieu
 * (stripe-b2b POST /referral/code) et son lien donnent 1 mois d'essai au filleul ; le parrain reçoit
 * un mois de crédit quand le filleul paie (stripe-b2b referral.js). Carte masquée si le code est
 * indisponible. Mesure : `account_referral_view`, `account_referral_copy` (`what`), `account_referral_share` (`channel`).
 */
export function ReferralCard({
    clientID,
    index,
    annualRenewal = null,
}: {
    clientID: string;
    index: number;
    /** Abonnement annuel : fin de période (s) = date à laquelle les mois offerts sont déduits. */
    annualRenewal?: number | null;
}) {
    const { t, i18n } = useTranslation();
    const [referral, setReferral] = useState<ReferralCode | null>(null);
    const [copied, setCopied] = useState<'code' | 'link' | null>(null);
    const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    useEffect(() => {
        let alive = true;
        getMyReferralCode(clientID).then((r) => {
            if (!alive) return;
            setReferral(r);
            if (r) logEvent('account_referral_view', 'Account', { clientID });
        });
        return () => {
            alive = false;
        };
    }, [clientID]);

    if (!referral) return null;
    const { code } = referral;
    const link = referralLink(code);
    const message = t('account.referral.shareMessage', { link, code });

    const copy = async (what: 'code' | 'link') => {
        logEvent('account_referral_copy', 'Account', { clientID, what });
        try {
            await navigator.clipboard.writeText(what === 'code' ? code : link);
            setCopied(what);
            window.setTimeout(() => setCopied(null), 2500);
        } catch {
            // Presse-papiers refusé (iframe, vieux navigateur) : le lien reste sélectionnable.
            (document.getElementById('referral-link') as HTMLInputElement | null)?.select();
        }
    };

    const share = async () => {
        logEvent('account_referral_share', 'Account', { clientID, channel: 'native' });
        try {
            await navigator.share({ text: message });
        } catch {
            /* partage annulé */
        }
    };

    return (
        <section className={styles.card} style={{ '--i': index } as React.CSSProperties}>
            <div className={styles.cardHead}>
                <div className={styles.cardIcon}>
                    <IconGift />
                </div>
                <div>
                    <div className={styles.cardTitle}>{t('account.referral.title')}</div>
                </div>
            </div>
            <p className={own.body}>{t('account.referral.body')}</p>
            <div className={own.codeRow}>
                <div>
                    <div className={own.codeLabel}>{t('account.referral.codeLabel')}</div>
                    <div className={own.code}>{code}</div>
                </div>
                <button type="button" className={styles.secondaryBtn} onClick={() => copy('code')}>
                    {copied === 'code' ? t('account.referral.copied') : t('account.referral.copyCode')}
                </button>
            </div>
            <div className={own.linkRow}>
                <input
                    id="referral-link"
                    className={`${styles.input} ${own.linkInput}`}
                    value={link}
                    readOnly
                    onFocus={(e) => e.currentTarget.select()}
                    aria-label={t('account.referral.linkLabel')}
                />
                <button type="button" className={styles.saveBtn} onClick={() => copy('link')}>
                    {copied === 'link' ? t('account.referral.copied') : t('account.referral.copy')}
                </button>
            </div>
            <div className={own.shareRow}>
                {canShare && (
                    <button type="button" className={styles.secondaryBtn} onClick={share}>
                        {t('account.referral.share')}
                    </button>
                )}
                <a
                    className={styles.secondaryBtn}
                    href={`https://wa.me/?text=${encodeURIComponent(message)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => logEvent('account_referral_share', 'Account', { clientID, channel: 'whatsapp' })}
                >
                    WhatsApp
                </a>
                <a
                    className={styles.secondaryBtn}
                    href={`mailto:?subject=${encodeURIComponent(t('account.referral.emailSubject'))}&body=${encodeURIComponent(message)}`}
                    onClick={() => logEvent('account_referral_share', 'Account', { clientID, channel: 'email' })}
                >
                    {t('account.referral.email')}
                </a>
            </div>
            {referral.rewards && referral.rewards.count > 0 && (
                <p className={own.earned}>
                    {referral.rewards.currency && referral.rewards.amountCents
                        ? t('account.referral.earned', {
                              months: referral.rewards.count,
                              amount: formatAmount(
                                  referral.rewards.amountCents,
                                  referral.rewards.currency,
                                  i18n.language
                              ),
                          })
                        : t('account.referral.earnedNoAmount', { months: referral.rewards.count })}
                </p>
            )}
            <p className={styles.hint}>
                {annualRenewal
                    ? t('account.referral.rulesAnnual', { date: unixDate(annualRenewal, i18n.language) })
                    : t('account.referral.rules')}
            </p>
        </section>
    );
}
